import React, { useState } from 'react';
import { Users, Clock, ArrowRight, Calendar, MapPin, ExternalLink, Sparkles, Timer } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ZOOM_LINK = "https://us04web.zoom.us/j/6118079912?pwd=G6s8LmySCXksp1D2l9wtzIXQ8guj2s.1&omn=77279878246";

export const WorkshopsPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/waitlist`, { name: name.trim(), email: email.trim() });
      toast.success(res.data.message);
      setJoined(true);
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const futureTopics = [
    { title: "Breaking the Silence", desc: "A safe group space for women to share their stories and begin the journey of speaking their truth." },
    { title: "Releasing Shame & Self-Blame", desc: "Practical exercises and faith-based tools to release the shame that was never yours to carry." },
    { title: "Boundaries After Abuse", desc: "Learn to set, communicate, and hold healthy boundaries in every area of your life." },
    { title: "From Surviving to Thriving", desc: "Moving beyond survival mode and stepping into the abundant life God has for you." }
  ];

  return (
    <div data-testid="workshops-page" className="min-h-screen bg-cream">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-b from-charcoal to-sage-dark py-16 px-6 text-center text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-8 left-12 w-32 h-32 border border-gold/30 rounded-full" />
          <div className="absolute bottom-8 right-12 w-48 h-48 border border-gold/20 rounded-full" />
        </div>
        <div className="relative max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/20 rounded-full mb-5">
            <Sparkles className="w-4 h-4 text-gold" />
            <span className="text-sm font-semibold text-gold uppercase tracking-wider">Live Workshop</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-4">
            Healing Workshops
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto">
            Group workshops designed specifically for women overcoming sexual abuse and trauma. 
            Heal in community. You are not alone.
          </p>
        </div>
      </section>

      {/* Featured Live Workshop */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div data-testid="live-workshop" className="relative bg-white rounded-3xl shadow-sm border border-sage/20 overflow-hidden">
            {/* Top banner */}
            <div className="bg-gradient-to-r from-sage to-sage-dark px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 bg-gold rounded-full animate-pulse" />
                <span className="text-white text-sm font-semibold uppercase tracking-wider">Booking Now Open</span>
              </div>
              <span className="text-white/70 text-sm">Limited to 15 spaces</span>
            </div>

            <div className="p-8 md:p-10">
              <h2 className="font-heading text-3xl sm:text-4xl text-charcoal mb-3">
                Breakthrough
              </h2>
              <p className="text-charcoal-light text-base md:text-lg mb-8 max-w-2xl">
                A space for women to feel inspired and empowered whilst overcoming sexual abuse trauma. 
                Join us for a powerful, faith-led session designed to help you take your next step toward healing.
              </p>

              {/* Details grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-cream-paper rounded-xl p-4 text-center">
                  <Calendar className="w-5 h-5 text-sage mx-auto mb-2" />
                  <p className="text-xs text-charcoal-light uppercase tracking-wider mb-1">Date</p>
                  <p className="font-semibold text-charcoal text-sm">8th August 2026</p>
                </div>
                <div className="bg-cream-paper rounded-xl p-4 text-center">
                  <Clock className="w-5 h-5 text-sage mx-auto mb-2" />
                  <p className="text-xs text-charcoal-light uppercase tracking-wider mb-1">Time</p>
                  <p className="font-semibold text-charcoal text-sm">1:00 PM GMT</p>
                </div>
                <div className="bg-cream-paper rounded-xl p-4 text-center">
                  <Timer className="w-5 h-5 text-sage mx-auto mb-2" />
                  <p className="text-xs text-charcoal-light uppercase tracking-wider mb-1">Duration</p>
                  <p className="font-semibold text-charcoal text-sm">1.5 Hours</p>
                </div>
                <div className="bg-cream-paper rounded-xl p-4 text-center">
                  <MapPin className="w-5 h-5 text-sage mx-auto mb-2" />
                  <p className="text-xs text-charcoal-light uppercase tracking-wider mb-1">Location</p>
                  <p className="font-semibold text-charcoal text-sm">Online (Zoom)</p>
                </div>
              </div>

              {/* Price + CTA */}
              <div className="flex flex-col sm:flex-row items-center gap-4 justify-between bg-gradient-to-r from-sage/5 to-gold-light/10 rounded-2xl p-6 border border-sage/10">
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-heading text-4xl text-sage font-bold">£15</span>
                    <span className="text-charcoal-light text-sm">per person</span>
                  </div>
                  <p className="text-xs text-charcoal-light mt-1 flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" /> Maximum 15 attendees — intimate & safe
                  </p>
                </div>
                <a
                  href={ZOOM_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-sage text-white rounded-full font-bold text-lg hover:bg-sage-dark transition-all shadow-lg hover:shadow-xl"
                  data-testid="workshop-book-btn"
                >
                  <ExternalLink className="w-5 h-5" /> Book Your Space
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Future Workshop Topics */}
      <section className="py-16 px-6 bg-sage/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl text-charcoal text-center mb-3">Future Workshop Topics</h2>
          <p className="text-charcoal-light text-center mb-10 max-w-lg mx-auto">Join the waitlist to be first to know when these workshops are announced.</p>
          <div className="grid sm:grid-cols-2 gap-6">
            {futureTopics.map((t, i) => (
              <div key={i} data-testid={`workshop-topic-${i+1}`} className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-sage" />
                  </div>
                  <h3 className="font-heading text-lg text-charcoal">{t.title}</h3>
                </div>
                <p className="text-sm text-charcoal-light">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist */}
      <section className="py-16 px-6">
        <div className="max-w-lg mx-auto text-center">
          <h2 className="font-heading text-3xl text-charcoal mb-4">Join the Waitlist</h2>
          <p className="text-charcoal-light mb-8">
            Be the first to know when future workshop dates are announced. We'll email you with dates, details, and early-bird pricing.
          </p>

          {joined ? (
            <div data-testid="waitlist-success" className="bg-white rounded-2xl p-8 shadow-sm">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-sage/10 flex items-center justify-center">
                <Users className="w-8 h-8 text-sage" />
              </div>
              <h3 className="font-heading text-xl text-charcoal mb-2">You're on the list!</h3>
              <p className="text-sm text-charcoal-light">We'll let you know as soon as new workshops open for registration.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="waitlist-form" className="bg-white rounded-2xl p-8 shadow-sm space-y-4">
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                data-testid="waitlist-name"
                className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sage/30"
              />
              <input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                data-testid="waitlist-email"
                className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sage/30"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="waitlist-submit"
                className="w-full py-3 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all disabled:opacity-50"
              >
                {loading ? 'Joining...' : 'Join the Waitlist'}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};
